import { useState } from 'react';
import { Link } from 'react-router';
import { Globe, Terminal, Hash, Send, GitBranch, Trash2, PlayCircle } from 'lucide-react';
import type { DashboardRunResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDuration, formatStarted } from '@/lib/format';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { runStatusMeta } from '@/lib/workflow-status';

interface WorkflowHistoryTableProps {
  runs: DashboardRunResponse[];
  onResume?: (runId: string) => void | Promise<void>;
  onDelete?: (runId: string) => void;
}

/** Failed/paused runs can be resumed (skips already-completed nodes). */
function isResumable(status: string): boolean {
  return status === 'failed' || status === 'paused';
}

const PLATFORM_ICONS: Record<string, React.ReactElement> = {
  web: <Globe className="h-3 w-3" />,
  cli: <Terminal className="h-3 w-3" />,
  slack: <Hash className="h-3 w-3" />,
  telegram: <Send className="h-3 w-3" />,
  github: <GitBranch className="h-3 w-3" />,
};

export function WorkflowHistoryTable({
  runs,
  onResume,
  onDelete,
}: WorkflowHistoryTableProps): React.ReactElement {
  const { confirm, confirmDialog } = useConfirm();
  // Per-row in-flight guard: disables the Resume button so a double-press can't
  // dispatch a second rerun before the first lands.
  const [resumingId, setResumingId] = useState<string | null>(null);
  if (runs.length === 0) {
    return (
      <>
        <div className="flex items-center justify-center py-8">
          <span className="text-xs text-text-tertiary">No history</span>
        </div>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-[22px] border border-border/70 bg-surface/95 shadow-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border/70 bg-surface-elevated/80 text-left text-text-tertiary">
              <th className="px-3 py-2 font-medium w-8">Status</th>
              <th className="px-3 py-2 font-medium">Workflow</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium w-16">Source</th>
              <th className="px-3 py-2 font-medium w-20">Duration</th>
              <th className="px-3 py-2 font-medium w-32">Started</th>
              <th className="px-3 py-2 font-medium w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {runs.map(run => (
              <tr
                key={run.id}
                className={cn(
                  'transition-colors hover:bg-surface-elevated/60',
                  run.status === 'failed' && 'border-l-2 border-l-error'
                )}
              >
                <td className="px-3 py-2">
                  <div className={cn('h-2 w-2 rounded-full', runStatusMeta(run.status).dotClass)} />
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/workflows/runs/${run.id}`}
                    className="block truncate font-medium text-text-primary hover:text-primary"
                  >
                    {run.workflow_name}
                  </Link>
                  {run.user_message && (
                    <p className="text-[11px] text-text-tertiary truncate max-w-[300px]">
                      {run.user_message}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-text-secondary truncate">
                  {run.codebase_name ?? '\u2014'}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/70 px-2 py-1 text-text-secondary">
                    {PLATFORM_ICONS[run.platform_type ?? ''] ?? null}
                    {run.platform_type ?? '\u2014'}
                  </span>
                </td>
                <td className="px-3 py-2 text-text-secondary">
                  {formatDuration(run.started_at, run.completed_at)}
                </td>
                <td className="px-3 py-2 text-text-secondary">{formatStarted(run.started_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/workflows/runs/${run.id}`}
                      className="rounded-xl border border-primary/20 bg-primary/5 px-2.5 py-1 text-primary transition-colors hover:text-primary/80"
                    >
                      View Logs
                    </Link>
                    {onResume && isResumable(run.status) && (
                      <button
                        onClick={(): void => {
                          setResumingId(run.id);
                          void Promise.resolve(onResume(run.id)).finally(() => {
                            setResumingId(current => (current === run.id ? null : current));
                          });
                        }}
                        disabled={resumingId === run.id}
                        className="inline-flex items-center gap-1 rounded-xl border border-primary/20 bg-primary/5 px-2.5 py-1 text-primary transition-colors hover:text-primary/80 disabled:opacity-50"
                        title="Resume run (skips already-completed nodes)"
                      >
                        <PlayCircle className="h-3 w-3" />
                        {resumingId === run.id ? 'Resuming...' : 'Resume'}
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={(): void => {
                          void confirm({
                            title: `Delete workflow run "${run.workflow_name}"?`,
                            description: 'This permanently deletes the run record and its logs.',
                            risk: 'high',
                            confirmLabel: 'Delete',
                          }).then(ok => {
                            if (ok) onDelete(run.id);
                          });
                        }}
                        className="text-text-tertiary hover:text-error transition-colors"
                        title="Delete run"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmDialog}
    </>
  );
}
